import { useCallback, useState } from 'react';

import { EMPTY_MESSAGES } from '@/shared/lib/constants';
import { sortTagsSafe } from '@/shared/lib/sort';
import { BadgeList } from '@/shared/ui/BadgeList';
import { Icon } from '@/shared/ui/Icon';
import { IconButton } from '@/shared/ui/IconButton';

import { useDagDashboardDetail, useUpdateDagMutation } from '@/entities/dag';

import { DagTagEditModal } from '@/features/dag-detail/ui/DagTagEditModal';

import styles from './DagTagSection.module.css';

type DagTagSectionProps = {
  dagId: string;
};

export const DagTagSection = ({ dagId }: DagTagSectionProps) => {
  const { data: dagDashboardDetail } = useDagDashboardDetail(dagId ?? '');
  const { mutateAsync: updateDag, isPending: isUpdatePending } = useUpdateDagMutation();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const currentTags = dagDashboardDetail.tags ?? [];
  const sortedTags = sortTagsSafe(currentTags);
  const tagItems = sortedTags.map((tag) => ({
    tagFQN: tag,
    name: tag,
    displayName: tag,
  }));

  const handleOpenEditModal = useCallback(() => {
    setIsEditModalOpen(true);
  }, []);

  const handleCloseEditModal = useCallback(() => {
    if (isUpdatePending) {
      return;
    }

    setIsEditModalOpen(false);
  }, [isUpdatePending]);

  const handleSubmitTags = useCallback(
    async (tags: string[]) => {
      await updateDag({
        dagUuid: dagId,
        tags,
      });

      setIsEditModalOpen(false);
    },
    [dagId, updateDag]
  );

  return (
    <>
      <div className={styles.tagSectionHeader}>
        <h2 className={styles.tagSectionTitle}>DAG 태그</h2>
        <IconButton onClick={handleOpenEditModal} ariaLabel="태그 수정" tooltip="태그 수정">
          <Icon name="edit" size={16} />
        </IconButton>
      </div>
      {tagItems.length > 0 ? (
        <BadgeList items={tagItems} />
      ) : (
        <p className={styles.emptyTagList}>{EMPTY_MESSAGES.NO_CONNECTED_TAGS}</p>
      )}
      <DagTagEditModal
        open={isEditModalOpen}
        tags={currentTags}
        onClose={handleCloseEditModal}
        onSubmit={handleSubmitTags}
        confirmLoading={isUpdatePending}
      />
    </>
  );
};
